import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AILoader } from '@/components/AILoader';
import { TRACK_LABELS } from '@/lib/tracks';
import { Timer as TimerIcon, ListChecks, Sparkles } from 'lucide-react';

export default function PreTestPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const track = sp.get('track');
  const klass = Number(sp.get('klass'));
  const subject = sp.get('subject');
  const chapter = sp.get('chapter');
  const setNumber = Number(sp.get('set_number'));
  const [starting, setStarting] = useState(false);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post('/test/start', {
        track,
        klass,
        subject,
        chapter,
        set_number: setNumber,
      });
      nav(`/test/${data.session_id}`, { state: { session: data } });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to start test. Try again.';
      if (err?.response?.status === 402) {
        toast.error(detail);
        // send them back to chapter hub to unlock / buy
        const params = new URLSearchParams({ track, klass: String(klass), subject, chapter });
        nav(`/chapter?${params.toString()}`);
        return;
      }
      toast.error(detail);
      setStarting(false);
    }
  };

  if (starting) {
    return (
      <AppShell>
        <AILoader chapter={chapter} subject={subject} setNumber={setNumber} track={TRACK_LABELS[track] || track} />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="pre-test-page">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Ready to begin</div>
          <h1 className="font-display text-3xl font-semibold">{chapter}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="secondary">{TRACK_LABELS[track] || track}</Badge>
            <Badge variant="secondary">Class {klass}</Badge>
            <Badge variant="secondary">{subject}</Badge>
            <Badge variant="secondary">Set {setNumber}</Badge>
          </div>
        </div>

        <Card>
          <CardContent className="grid gap-4 p-6 sm:grid-cols-3">
            <Stat icon={ListChecks} label="Questions" value="35" />
            <Stat icon={TimerIcon} label="Timing" value="Stopwatch" />
            <Stat icon={Sparkles} label="Style" value="PYQ + Module" />
          </CardContent>
        </Card>

        <Alert>
          <AlertTitle>Test rules</AlertTitle>
          <AlertDescription className="mt-2">
            <ul className="list-inside list-disc space-y-1 text-sm">
              <li>Stopwatch starts the moment you click Start — take your time, but keep an eye on it.</li>
              <li>Every question has 4 options and exactly one correct answer.</li>
              <li>You can mark questions for review and revisit them via the palette.</li>
              <li>Diagrams appear inline where relevant — read them carefully.</li>
            </ul>
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap items-center gap-3">
          <Button className="h-11 gap-2 px-6" onClick={start} data-testid="start-test-button">
            <Sparkles className="h-4 w-4" />
            Start Test
          </Button>
          <Button variant="ghost" onClick={() => nav(-1)} data-testid="pre-test-back-button">
            ← Back
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-secondary/40 p-3">
      <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="font-display text-lg font-semibold">{value}</div>
      </div>
    </div>
  );
}
